// public/js/features/pois/poi-state.js
// Shared POI state so the sidebar submenu (search / checkboxes / add) and the
// map renderer stay in sync. Disabled public types (turned off for the tenant)
// are filtered out at load time and never reach the map or the checkbox list.
//
// The set of enabled types is the user's filter; it is persisted to (and
// restored from) localStorage so it survives reloads — see poi-cache.js.
import { readEnabledTypes, writeEnabledTypes } from './poi-cache.js';
import { getScope } from '../../modules/tenant-scope.js';

// Global tenant scope (docs/tenant-scope.md): public POIs are always visible;
// tenant-owned POIs/types only when their tenant is in the active scope.
// setData re-runs on every page init (cache-first), so a scope change
// re-filters naturally.
function scopeAllowsPoi(itemScope) {
  if (!itemScope || itemScope === 'public') return true;
  const s = getScope();
  if (s.all) return true;
  if (s.unassigned) return false;
  return itemScope === s.tenantId;
}

export const poiState = {
  types: [],            // visible & available types (disabled public types removed)
  pois: [],             // all visible pins
  enabledTypeIds: new Set(), // types currently shown on the map
  placeMode: false,
  loaded: false,
  _subs: [],

  setData(types = [], pois = []) {
    const available = types.filter(t => !t.disabled && scopeAllowsPoi(t.scope));
    this.types = available;
    this.pois = pois.filter(p => scopeAllowsPoi(p.scope));

    const availableIds = new Set(available.map(t => t.poiId));
    const saved = readEnabledTypes(); // array of typeIds, or null if never set

    if (saved !== null) {
      // Restore the persisted filter; keep only types that still exist. New
      // types stay OFF so their pins aren't loaded/shown until the user opts in.
      this.enabledTypeIds = new Set(saved.filter(id => availableIds.has(id)));
    } else if (!this.loaded) {
      // First ever load with no saved preference → show everything and persist.
      this.enabledTypeIds = new Set(availableIds);
      writeEnabledTypes(this.enabledTypeIds);
    } else {
      // Keep current choices, drop types that disappeared.
      const next = new Set();
      availableIds.forEach(id => { if (this.enabledTypeIds.has(id)) next.add(id); });
      this.enabledTypeIds = next;
    }
    this._known = availableIds;
    this.loaded = true;
    this.emit();
  },

  typeById(id) {
    return this.types.find(t => t.poiId === id) || null;
  },

  visiblePois() {
    return this.pois.filter(p => p.typeId && this.enabledTypeIds.has(p.typeId));
  },

  toggleType(id, on) {
    if (on) this.enabledTypeIds.add(id);
    else this.enabledTypeIds.delete(id);
    writeEnabledTypes(this.enabledTypeIds); // persist the filter change
    this.emit();
  },

  setPlaceMode(on) { this.placeMode = !!on; },

  onChange(cb) { this._subs.push(cb); },
  emit() { this._subs.forEach(cb => { try { cb(); } catch (e) { console.warn('[poiState] sub error', e); } }); },
};

// Internal: set of type ids known at the previous load (for default-on logic).
poiState._known = new Set();
